import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { BackLink, Banner } from '../ui/components.tsx'
import { Picto } from '../ui/pictos.tsx'

/** The privacy notice (M11 part D; ARCHITECTURE 16.2): what the app records about the people who use it, why, who sees it, how long it is kept and the
 *  rights. The same content as docs/privacy/information-salaries.md (the notice for display and printing): change them together. It is factual: the
 *  controller's identity is a placeholder the club fills in, never made up; whatever an adviser must confirm says so; the phone's position is said NOT to be
 *  recorded (the owner, 2026-10-27: location is off at the club, M21; no code path asks for a position while it is off; turning it on needs a new notice
 *  first, as Réglages › Localisation says), and the information sheet as M20 built it (the direction's,
 *  behind the second factor, every read audited, never on a device, removed 3 months after the last employment period; neither the legal staff register nor payroll),
 *  and the incident register as M16-G built it (the direction's, behind the second factor, every read audited, never on a device, five years after the closure "(à vérifier)"),
 *  and M24's forecast fetch (ADDED among the recipients: the server asks the German weather service for a public file by the point's code only, about once an hour).
 *  It reads nothing and needs no network.
 *  Its numbers are the code's: Privacy.test.tsx derives the retention periods from supabase/tests/fixtures/privacy_notice.json, which pgTAP 47 holds to ref.retention_category
 *  and pg_cron, and the sign-in and backup numbers from packages/contracts/src/platform.ts (M22: one processor, Hetzner, no other host and no host's backup):
 *  change the code and these texts together. Reached from Réglages (inside the app) and from the sign-in page (`standalone`: outside the router, signed out). */
export function PrivacyPage({ standalone = false }: { standalone?: boolean }) {
  const { t } = useTranslation()
  const back = standalone
    ? <BackLink href="/" testId="privacy-back">← {t('privacy.backToSignIn')}</BackLink>
    : <BackLink to="/settings" testId="privacy-back">← {t('privacy.backToSettings')}</BackLink>
  const body = (
    <article className="flex flex-col gap-4" data-testid="privacy">
      {back}
      <h1 className="flex items-center gap-2 text-2xl font-bold"><Picto name="clipboard" className="size-7 shrink-0 text-brand dark:text-fairway" />{t('privacy.title')}</h1>
      <p>{t('privacy.intro')}</p>
      <Banner tone="warn"><span data-testid="privacy-draft">{t('privacy.draft')}</span></Banner>

      <Section title={t('privacy.controller.title')}>
        <p>{t('privacy.controller.body')}</p>
        <address className="not-italic font-semibold" data-testid="privacy-controller">
          <span className="block">{t('privacy.controller.name')}</span>
          <span className="block">{t('privacy.controller.address')}</span>
          <span className="block">{t('privacy.controller.contact')}</span>
        </address>
        <p className="text-sm opacity-75">{t('privacy.controller.fill')}</p>
      </Section>

      <Section title={t('privacy.data.title')}>
        <ul className="flex flex-col gap-3">
          <Item label={t('privacy.data.accountLabel')}>{t('privacy.data.account')}</Item>
          <Item label={t('privacy.data.timeLabel')}>{t('privacy.data.time')}</Item>
          <Item label={t('privacy.data.machinesLabel')}>{t('privacy.data.machines')}</Item>
          <Item label={t('privacy.data.qualificationsLabel')}>{t('privacy.data.qualifications')}</Item>
          <Item label={t('privacy.data.photosLabel')}>{t('privacy.data.photos')}</Item>
          <Item label={t('privacy.data.positionsLabel')} testId="privacy-positions">{t('privacy.data.positions')}</Item>
          <Item label={t('privacy.data.payLabel')}>{t('privacy.data.pay')}</Item>
          <Item label={t('privacy.data.staffFileLabel')} testId="privacy-staff-file">{t('privacy.data.staffFile')}</Item>
          <Item label={t('privacy.data.treatmentLabel')} testId="privacy-treatment">{t('privacy.data.treatment')}</Item>
          <Item label={t('privacy.data.stockLabel')} testId="privacy-stock">{t('privacy.data.stock')}</Item>
          <Item label={t('privacy.data.incidentLabel')} testId="privacy-incident">{t('privacy.data.incident')}</Item>
          <Item label={t('privacy.data.logsLabel')}>{t('privacy.data.logs')}</Item>
        </ul>
        <p>{t('privacy.data.none')}</p>
        <p data-testid="privacy-not-register">{t('privacy.data.notRegister')}</p>
      </Section>

      <Section title={t('privacy.bases.title')}>
        <ul className="flex list-disc flex-col gap-2 pl-5">
          <li>{t('privacy.bases.legal')}</li>
          <li>{t('privacy.bases.contract')}</li>
          <li>{t('privacy.bases.interest')}</li>
        </ul>
        <p className="text-sm opacity-75">{t('privacy.bases.note')}</p>
      </Section>

      <Section title={t('privacy.recipients.title')}>
        <p>{t('privacy.recipients.club')}</p>
        <p data-testid="privacy-exports">{t('privacy.recipients.exports')}</p>
        <p className="font-semibold">{t('privacy.recipients.processors')}</p>
        <ul className="flex list-disc flex-col gap-2 pl-5" data-testid="privacy-processors">
          <li>{t('privacy.recipients.hetzner')}</li>
        </ul>
        <p className="text-sm opacity-75">{t('privacy.recipients.later')}</p>
        <p>{t('privacy.recipients.none')}</p>
        {/* M24 (ADR-125): the server's one call out, the forecast: about once an hour, the point's code only, nothing about a person; the provider's logs and how long
            the club keeps the attempts, the original files and the forecasts (privacy_notice.json forecast_fetch; no row of the table: they hold no person's value) */}
        <p data-testid="privacy-forecast">{t('privacy.recipients.forecast')}</p>
      </Section>

      <Section title={t('privacy.retention.title')}>
        <p>{t('privacy.retention.intro')}</p>
        <dl className="flex flex-col divide-y divide-slate-200 dark:divide-night-line" data-testid="privacy-retention" aria-label={`${t('privacy.retention.category')} / ${t('privacy.retention.period')}`}>
          {/* the nine categories of ref.retention_category (M11 part A's job, M20's departed employee's information sheet after the person's record, M15-C's treatment register, M16-G's incident register; their periods
              are held to the code by Privacy.test.tsx and pgTAP 47 through supabase/tests/fixtures/privacy_notice.json), the auth module's sessions and failure counters
              (M22: SESSION_IDLE_DAYS, AUTH_LOG_KEEP_HOURS of packages/contracts/src/platform.ts), the club's backups (BACKUP_KEEP_NIGHTS), then what no category
              covers, so that the table leaves nothing out */}
          <Row k={t('privacy.retention.diagnostics')} v={t('privacy.retention.diagnosticsPeriod')} />
          <Row k={t('privacy.retention.access')} v={t('privacy.retention.accessPeriod')} />
          <Row k={t('privacy.retention.signins')} v={t('privacy.retention.signinsPeriod')} />
          <Row k={t('privacy.retention.rejected')} v={t('privacy.retention.rejectedPeriod')} />
          <Row k={t('privacy.retention.time')} v={t('privacy.retention.timePeriod')} />
          <Row k={t('privacy.retention.safety')} v={t('privacy.retention.safetyPeriod')} />
          <Row k={t('privacy.retention.audit')} v={t('privacy.retention.auditPeriod')} />
          <Row k={t('privacy.retention.backups')} v={t('privacy.retention.backupsPeriod')} />
          <Row k={t('privacy.retention.person')} v={t('privacy.retention.personPeriod')} />
          <Row k={t('privacy.retention.staffFile')} v={t('privacy.retention.staffFilePeriod')} />
          <Row k={t('privacy.retention.treatment')} v={t('privacy.retention.treatmentPeriod')} />
          <Row k={t('privacy.retention.incident')} v={t('privacy.retention.incidentPeriod')} />
          <Row k={t('privacy.retention.photos')} v={t('privacy.retention.photosPeriod')} />
          <Row k={t('privacy.retention.other')} v={t('privacy.retention.otherPeriod')} />
        </dl>
        <p>{t('privacy.retention.leaving')}</p>
      </Section>

      <Section title={t('privacy.rights.title')}>
        <ul className="flex list-disc flex-col gap-2 pl-5" data-testid="privacy-rights">
          <li>{t('privacy.rights.access')}</li>
          <li>{t('privacy.rights.rectify')}</li>
          <li>{t('privacy.rights.erase')}</li>
          <li>{t('privacy.rights.object')}</li>
          <li>{t('privacy.rights.complaint')}</li>
        </ul>
      </Section>

      <Section title={t('privacy.exercise.title')}>
        <p>{t('privacy.exercise.body')}</p>
      </Section>
      <p className="text-sm opacity-75">{t('privacy.updated')}</p>
    </article>
  )
  return standalone ? <main className="mx-auto min-h-dvh max-w-2xl p-4 sm:p-6">{body}</main> : body
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return <section className="card flex flex-col gap-3"><h2 className="text-lg font-bold">{title}</h2>{children}</section>
}
function Item({ label, children, testId }: { label: string; children: ReactNode; testId?: string }) {
  return <li data-testid={testId}><strong className="font-semibold">{label}</strong> {children}</li>
}
function Row({ k, v }: { k: string; v: string }) {
  return <div className="flex flex-col gap-0.5 py-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4"><dt>{k}</dt><dd className="font-semibold sm:text-right">{v}</dd></div>
}
